import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ESTADO_CASO_PILL } from "@/lib/estadoCaso";
import { ApiError, api, type CasoListItem } from "@/lib/api";
import { useToast } from "@/ui/Toast";


/**
 * Panel "Hilo del caso": muestra los casos relacionados (padre transitivo +
 * hijos transitivos) en orden cronologico. Solo se renderiza si hay al menos
 * un caso relacionado.
 */
export default function PanelHilo({
  casoId,
  refreshKey,
}: {
  casoId: number;
  /** Cambialo para forzar re-fetch (ej. tras crear un caso hijo). */
  refreshKey?: number;
}) {
  const toast = useToast();
  const [items, setItems] = useState<CasoListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<CasoListItem[]>(`/api/casos/${casoId}/hilo`)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude cargar el hilo del caso", msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId, refreshKey]);

  if (loading || items.length === 0) return null;

  return (
    <div className="card space-y-2">
      <h3 className="text-xs uppercase tracking-wide text-zinc-400">
        Hilo del caso ({items.length} relacionado{items.length === 1 ? "" : "s"})
      </h3>
      <ul className="divide-y divide-ink-700">
        {items.map((c) => (
          <li key={c.id} className="py-2">
            <Link
              to={`/casos/${c.folio}`}
              className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3 hover:bg-ink-800/40 rounded px-2 py-1 -mx-2"
            >
              <div className="flex items-center gap-2 min-w-0 flex-wrap sm:flex-nowrap">
                <span className="font-mono text-accent-400 text-sm truncate">
                  {c.folio}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border whitespace-nowrap ${ESTADO_CASO_PILL[c.estado]}`}
                >
                  {c.estado}
                </span>
                {(c.marca || c.modelo) && (
                  <span className="text-xs text-zinc-400 truncate">
                    {c.marca || ""} {c.modelo || ""}
                  </span>
                )}
              </div>
              <div className="text-xs text-zinc-500 whitespace-nowrap flex-shrink-0">
                {new Date(c.created_at).toLocaleDateString()}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
